import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Component, computed, EventEmitter, inject, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { Avatar } from 'primeng/avatar';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { Button } from 'primeng/button';
import { Menu } from 'primeng/menu';
import { AuthStore } from '../../auth/services/auth.store';
import { ThemeService } from '../../shared/services/theme.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [CommonModule, Menu, Avatar, Button, NgOptimizedImage],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.css',
})
export class NavbarComponent {
  private readonly authStore = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly themeService = inject(ThemeService);

  @Input() isMobile = false;
  @Output() toggleSidebarEvent = new EventEmitter<void>();

  readonly userWorkspace = computed(() => this.authStore.defaultWorkspace()?.name ?? '');
  readonly isOwner = this.authStore.isOwner;

  readonly workspaceActions = computed<MenuItem[]>(() => {
    const workspace = this.authStore.defaultWorkspace();

    if (!workspace) return [];

    return [
      {
        label: 'Ustawienia workspace',
        icon: PrimeIcons.COG,
        routerLink: ['workspaces', workspace.id, 'settings'],
      },
    ];
  });

  readonly logoUrl = computed(() => this.authStore.defaultWorkspace()?.logoUrl ?? '');

  readonly avatarItems = computed<MenuItem[]>(() => {
    const user = this.authStore.user();
    const name = user
      ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || 'No Name'
      : 'No Name';
    const email = user?.email ?? 'no.name@email.com';

    return [
      { label: name, styleClass: 'username-item' },
      { label: email, styleClass: 'email-item', disabled: true },
      { separator: true },
      { label: 'Profil', icon: PrimeIcons.USER, command: () => this.goToProfile() },
      {
        label: this.themeService.isDark() ? 'Jasny motyw' : 'Ciemny motyw',
        icon: this.themeService.isDark() ? PrimeIcons.SUN : PrimeIcons.MOON,
        command: () => this.toggleTheme(),
      },
      { separator: true },
      { label: 'Wyloguj się', icon: PrimeIcons.SIGN_OUT, command: () => this.logout() },
    ];
  });

  get userAvatarLabel(): string {
    const user = this.authStore.user();
    if (user?.firstName && user?.lastName) {
      return user.firstName.charAt(0).toUpperCase() + user.lastName.charAt(0).toUpperCase();
    }
    return 'U';
  }

  toggleSidebar(): void {
    this.toggleSidebarEvent.emit();
  }

  logout(): void {
    this.authStore.logout();
  }

  goToProfile(): void {
    this.router.navigateByUrl('/profile');
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  toggleAvatarMenu(event: MouseEvent, menu: Menu): void {
    event.stopPropagation();
    menu.toggle(event);
  }
}
